import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, TouchableOpacity } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { User } from '@graphql/generated';
import useMe from '@hooks/useMe';

type MessageMemberOverlayProps = {
  visible: boolean;
  onClose: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filteredMembers: User[];
  selectedMembers: string[];
  onToggleMemberSelection: (userId: string) => void;
  onToggleAllMembersSelection: () => void;
  onCreateChat: () => void;
  overlayRef?: React.RefObject<HTMLDivElement | null>;
  outsideClickRef?: { current: HTMLDivElement | null | undefined };
  position?: { top: number; left: number };
};

export const MessageMemberOverlay: React.FC<MessageMemberOverlayProps> = ({
  visible,
  onClose: _onClose,
  searchValue,
  onSearchChange,
  filteredMembers,
  selectedMembers,
  onToggleMemberSelection,
  onToggleAllMembersSelection,
  onCreateChat,
  overlayRef,
  outsideClickRef,
  position,
}) => {
  const { t } = useTranslation();
  const { me } = useMe();

  // Exclude current user from the members list
  const displayMembers = useMemo(() => {
    if (!me) return filteredMembers;

    // Filter out the current user from the list
    return filteredMembers.filter((member) => member.id !== me.id);
  }, [filteredMembers, me]);

  if (!visible) return null;

  const allFilteredMemberIds = displayMembers.map((m) => m.id);
  const allSelected =
    allFilteredMemberIds.length > 0 &&
    allFilteredMemberIds.every((id) => selectedMembers.includes(id));
  const someSelected =
    allFilteredMemberIds.some((id) => selectedMembers.includes(id)) &&
    !allSelected;

  const overlay = (
    <Box
      ref={(ref) => {
        if (ref && Platform.OS === 'web') {
          if (overlayRef) {
            overlayRef.current = ref as unknown as HTMLDivElement;
          }
          if (outsideClickRef) {
            outsideClickRef.current = ref as unknown as HTMLDivElement;
          }
        }
      }}
      style={{ ...position, position: 'fixed' } as any}
      zIndex={1000}>
      <ShadowBox
        width={380}
        maxHeight={500}
        backgroundColor='white'
        borderRadius='xs'
        variant='base'
        flexDirection='column'>
        <Box padding='m' borderBottomWidth={1} borderBottomColor='grey02'>
          <SearchInput
            onTextChanged={onSearchChange}
            value={searchValue}
            placeHolder='Search'
          />
        </Box>
        <ScrollView style={{ maxHeight: 350 }}>
          <Box paddingVertical='xs'>
            {/* All option */}
            <SimpleHoverable>
              {(isHovered) => (
                <TouchableOpacity onPress={onToggleAllMembersSelection}>
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    paddingVertical='xs'
                    paddingHorizontal='m'
                    backgroundColor={isHovered ? 'grey01' : 'transparent'}
                    // @ts-ignore - web-specific style prop
                    style={
                      Platform.OS === 'web'
                        ? ({
                            cursor: 'pointer',
                          } as any)
                        : undefined
                    }>
                    <Box marginRight='xs'>
                      {someSelected ? (
                        // Show minus text for partial selection
                        <Box
                          width={20}
                          height={20}
                          backgroundColor='greenSecondary'
                          borderRadius='xxs'
                          alignItems='center'
                          justifyContent='center'>
                          <Text variant='body' color='white' fontWeight='bold'>
                            –
                          </Text>
                        </Box>
                      ) : (
                        <CheckBoxButton checked={allSelected} />
                      )}
                    </Box>
                    <Text variant='metadata1'>All</Text>
                  </Box>
                </TouchableOpacity>
              )}
            </SimpleHoverable>

            {displayMembers.map((member) => {
              const isSelected = selectedMembers.includes(member.id);
              const memberName =
                member.name ||
                `${member.firstName || ''} ${member.lastName || ''}`.trim();
              return (
                <SimpleHoverable key={member.id}>
                  {(isHovered) => (
                    <TouchableOpacity
                      onPress={() => onToggleMemberSelection(member.id)}>
                      <Box
                        flexDirection='row'
                        alignItems='center'
                        paddingVertical='xs'
                        paddingHorizontal='m'
                        backgroundColor={isHovered ? 'grey01' : 'transparent'}
                        // @ts-ignore - web-specific style prop
                        style={
                          Platform.OS === 'web'
                            ? ({
                                cursor: 'pointer',
                              } as any)
                            : undefined
                        }>
                        <Box marginRight='xs'>
                          <CheckBoxButton checked={isSelected} />
                        </Box>
                        <Text variant='metadata1'>{memberName}</Text>
                      </Box>
                    </TouchableOpacity>
                  )}
                </SimpleHoverable>
              );
            })}
          </Box>
        </ScrollView>

        <Box
          paddingVertical='m'
          alignItems='center'
          borderTopWidth={1}
          borderTopColor='grey02'
          backgroundColor='white'>
          <SimpleHoverable>
            {(isHovered) => (
              <TouchableOpacity
                onPress={onCreateChat}
                disabled={selectedMembers.length === 0}>
                <Box
                  paddingHorizontal='m'
                  paddingVertical='xs'
                  borderRadius='xs'
                  backgroundColor={
                    isHovered && selectedMembers.length > 0
                      ? 'grey01'
                      : 'transparent'
                  }
                  alignSelf='center'
                  // @ts-ignore - web-specific style prop
                  style={
                    Platform.OS === 'web'
                      ? ({
                          cursor:
                            selectedMembers.length === 0
                              ? 'default'
                              : 'pointer',
                        } as any)
                      : undefined
                  }>
                  <Text
                    variant='labelSmall'
                    color={
                      selectedMembers.length === 0 ? 'grey04' : 'greenSecondary'
                    }>
                    {t('shared:startChat')}
                  </Text>
                </Box>
              </TouchableOpacity>
            )}
          </SimpleHoverable>
        </Box>
      </ShadowBox>
    </Box>
  );

  return createPortal(overlay, document.body);
};
